import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Sun, Send, Loader2, Clock, Bell, ImageIcon, FileText, Users } from "lucide-react";
import { toast } from "sonner";

export default function AdminDailyDigest() {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    const loadPreview = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/daily-digest/preview");
            setPreview(r.data);
        } catch (err) {
            toast.error("Failed to load preview");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadPreview(); }, []);

    const sendNow = async () => {
        if (!window.confirm("Send the daily digest to all active parents now?")) return;
        setSending(true);
        try {
            const r = await api.post("/admin/daily-digest/send-now");
            if (r.data.skipped) {
                toast.warning("Skipped — no new content in the last 24 hours.");
            } else {
                toast.success(`Digest sent to ${r.data.sent} parent(s)`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    const counts = preview?.counts || {};

    return (
        <div className="space-y-6" data-testid="admin-daily-digest-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Re-engagement</div>
                    <h1 className="font-heading text-3xl font-black tracking-tight mt-1 flex items-center gap-2">
                        <Sun className="h-7 w-7 text-primary" /> Daily Digest
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        A single morning email summarising yesterday&apos;s notifications, photos and documents.
                    </p>
                </div>
                <Button onClick={sendNow} disabled={sending} className="rounded-full" data-testid="digest-send-now-btn">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="h-4 w-4 mr-2" /> Send Digest Now</>)}
                </Button>
            </div>

            <Card className="rounded-2xl bg-primary/5 border-primary/30">
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Clock className="h-5 w-5 text-secondary-foreground" /> Automatic Schedule
                    </CardTitle>
                    <CardDescription>The digest is automatically sent every day at <strong>08:00 UTC</strong> (~13:30 IST).</CardDescription>
                </CardHeader>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat icon={Bell} label="Notifications (24h)" value={counts.notifications ?? 0} />
                <Stat icon={ImageIcon} label="Photos (24h)" value={counts.photos ?? 0} />
                <Stat icon={FileText} label="Documents (24h)" value={counts.documents ?? 0} />
                <Stat icon={Users} label="Active Parents" value={counts.active_parents ?? 0} />
            </div>

            <Card className="rounded-2xl">
                <CardHeader>
                    <CardTitle className="font-heading">Email Preview</CardTitle>
                    <CardDescription>This is what parents will see in their inbox tomorrow morning.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="border border-border rounded-2xl overflow-hidden bg-white" data-testid="digest-preview-html">
                        <iframe
                            title="Daily Digest Preview"
                            srcDoc={preview?.preview_html || ""}
                            className="w-full"
                            style={{ height: 720, border: 0 }}
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

const Stat = ({ icon: Icon, label, value }) => (
    <div className="bg-card border border-border rounded-2xl p-4" data-testid={`digest-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <div className="h-10 w-10 rounded-2xl bg-secondary flex items-center justify-center text-secondary-foreground">
            <Icon className="h-5 w-5" />
        </div>
        <div className="text-3xl font-black font-heading mt-3">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
);
